import { Loader, Modal } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconExclamationCircle } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Endpoints } from '../../../../../apis';
import { InstanceResponseModel } from '../../../../../apis/v1/instances/model';
import { Button, TagsField } from '../../../../../components/Atoms';
import { getErrorMessage } from '@xver/utils/error';

interface ManageTagsModalProps {
    opened: boolean;
    onClose: () => void;
    instance: InstanceResponseModel;
}

export default function ManageTagsModal({ opened, onClose, instance }: ManageTagsModalProps) {
    const { t } = useTranslation('cloudComputeInstance');
    const queryClient = useQueryClient();
    const [localTags, setLocalTags] = useState<string[]>([]);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (opened) {
            setLocalTags(instance.tags.map((t) => t.name));
        }
    }, [opened, instance.id]);

    const currentNames = instance.tags.map((t) => t.name);
    const toAdd = localTags.filter((t) => !currentNames.includes(t));
    const toDelete = instance.tags.filter((t) => !localTags.includes(t.name));
    const hasChanges = toAdd.length > 0 || toDelete.length > 0;

    const handleAdd = (tag: string) => {
        setLocalTags((prev) => [...prev, tag]);
    };

    const handleRemove = (tag: string) => {
        setLocalTags((prev) => prev.filter((x) => x !== tag));
    };

    const handleSave = async () => {
        if (!hasChanges || isSaving) return;
        setIsSaving(true);
        try {
            await Promise.all([
                ...toAdd.map((t) => Endpoints.v1.Instances.createInstanceTag(instance.id, t)),
                ...toDelete.map((t) => Endpoints.v1.Instances.deleteInstanceTag(instance.id, t.id)),
            ]);
            queryClient.invalidateQueries({ queryKey: ['api.v1.instances.get', instance.id] });
            queryClient.invalidateQueries({ queryKey: ['api.v1.instances.list'] });
            queryClient.invalidateQueries({ queryKey: ['api.v1.instances.tags', instance.id] });
            notifications.show({
                title: t('manageTags.updateSuccess'),
                message: t('manageTags.updateSuccessMessage'),
                icon: <IconCheck size={32} color={'#00ff00'} />,
            });
            onClose();
        } catch (err) {
            notifications.show({
                title: t('manageTags.updateFailed'),
                message: getErrorMessage(err),
                icon: <IconExclamationCircle size={32} color={'#eb5757'} />,
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal
            size="sm"
            opened={opened}
            onClose={onClose}
            withCloseButton={false}
            overlayProps={{
                backgroundOpacity: 0.55,
                blur: 3,
            }}
            transitionProps={{ transition: 'fade', duration: 128, timingFunction: 'linear' }}
            centered
        >
            <div className="flex flex-col justify-start items-start gap-4">
                <div className="flex flex-col justify-start items-start">
                    <Modal.Title>
                        <span className="text-base font-bold">{t('manageTags.title')}</span>
                    </Modal.Title>
                    <p className="text-sm text-fg/68">{t('manageTags.description')}</p>
                </div>
                <div className="w-full flex flex-col justify-start items-start gap-6">
                    <TagsField
                        label={t('manageTags.fieldLabel')}
                        value={localTags}
                        onAdd={handleAdd}
                        onRemove={handleRemove}
                        placeholder={t('manageTags.placeholder')}
                        description={t('manageTags.fieldDescription')}
                        maxTags={5}
                        disabled={isSaving}
                    />
                    <div className="w-full flex flex-col justify-start items-start gap-2.5">
                        <Button
                            width="100%"
                            onClick={handleSave}
                            aria-disabled={(!hasChanges && !isSaving) || undefined}
                            aria-busy={isSaving || undefined}
                            style={
                                !hasChanges && !isSaving
                                    ? { opacity: 0.5, cursor: 'not-allowed' }
                                    : undefined
                            }
                        >
                            {isSaving ? (
                                <div className="flex items-center gap-2">
                                    <Loader size={16} color="white" />
                                    <span>{t('manageTags.saving')}</span>
                                </div>
                            ) : (
                                t('manageTags.save')
                            )}
                        </Button>
                        <Button
                            width="100%"
                            variant="secondary"
                            onClick={onClose}
                            disabled={isSaving}
                        >
                            {t('manageTags.cancel')}
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
